// Очередь приёмки заявок. То, что финансовый директор разбирает после пульта.
//
// Цель по нажатиям из правил интерфейса: двадцать заявок за десять нажатий. Держится четырьмя
// вещами: галочка «отметить всё» считает по текущему отбору; быстрая кнопка «Принять» живёт
// в строке, а не за открытием панели; проблемные заявки (без чека, крупные) человек снимает
// с отметки, а не отмечает по одной; отклонение и возврат на доработку решаются тоже из
// строки, через меню «Ещё» рядом с «Принять» — до 15.09.2026 каждая такая заявка стоила
// открытия панели и съедала норму (подробности — в шапке `components/ClaimRow.tsx`).
//
// Оптимизма здесь нет ни на одном действии, и это осознанно: приёмка уменьшает остаток
// ДРУГОГО человека, заводит долг ТРЕТЬЕМУ и кладёт аванс в его строку ведомости. «Принято»
// до ответа сервера было бы неправдой. Мгновенно гаснет только кнопка самой строки — по
// ключу строки, а не общим флагом: общий флаг гасит все двадцать кнопок ради одной.

import { useState } from 'react';

import { RiFileList3Line, RiFilterOffLine } from '@remixicon/react';

import { ListPane } from '@/layout/ListPane';
import type { ClaimOut } from '@/shared/api/endpoints/accountable';
import type { ListResponse } from '@/shared/api/types';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { parseMoment } from '@/shared/time/format';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import * as Switch from '@/shared/ui/switch';

import type { РешениеСПричиной } from '../components/ClaimRow';
import { ClaimRow } from '../components/ClaimRow';
import { CLAIM_FORMS } from '../dictionaries';
import { useBulkAccept, useClaims, useDecideClaim } from '../queries';
import { FILTER_MIN_AMOUNT, FILTER_RECEIPT, FILTER_ROUND, RECEIPT_NONE, ROUND_ON } from '../route';

/** Приёмка и отклонение заявок: server/app/api/v1/accountable.py. */
const ACCEPT_PERMISSION = 'core.accountable.claim_accept';
/** Отклонение и возврат на доработку — своё право, не то же, что приёмка. */
const REJECT_PERMISSION = 'core.accountable.claim_reject';

/** Порог «крупная заявка» по умолчанию: пять тысяч рублей в копейках. */
const КРУПНАЯ = 500_000;

export interface ClaimsQueueProps {
  search: SearchState;
  onFilter: (key: string, value: string | null) => void;
  onOpenClaim: (claimId: string) => void;
}

export function ClaimsQueue({ search, onFilter, onOpenClaim }: ClaimsQueueProps) {
  // Готовое поле хранилища, а не `state.user?.permissions ?? []`: новый пустой массив на каждый
  // вызов селектора зацикливает отрисовку на zustand 5 (см. пояснение в MoneySection.tsx).
  const права = useAuthStore((state) => state.permissions);
  const можноПринимать = can(права, ACCEPT_PERMISSION);
  const можноОтклонять = can(права, REJECT_PERMISSION);

  const безЧека = search.filters[FILTER_RECEIPT] === RECEIPT_NONE;
  const отСуммы = search.filters[FILTER_MIN_AMOUNT];
  const круглые = search.filters[FILTER_ROUND] === ROUND_ON;

  const запрос = useClaims({
    status: ['submitted'],
    no_receipt: безЧека ? true : undefined,
    amount_from: отСуммы !== undefined ? Number(отСуммы) : undefined,
    round_only: круглые ? true : undefined,
  });
  const приёмка = useDecideClaim();
  const пачка = useBulkAccept();

  const [отмечено, setОтмечено] = useState<ReadonlySet<string>>(new Set());
  const [занята, setЗанята] = useState<string | null>(null);
  const [итогПачки, setИтогПачки] = useState<string | null>(null);

  const данные = запрос.data;
  const строки = данные?.items ?? [];
  const срез = данные?.generated_at !== undefined ? parseMoment(данные.generated_at) : null;
  // Отметки живут дольше списка: приняли одну заявку — она ушла из очереди, а её отметка
  // осталась. Кнопка тогда обещала «Принять 3» и принимала две, а галочка «отметить все»
  // гасла при полностью отмеченном списке. Считаем ВСЁ от видимых строк, а не от набора.
  const отмеченныеСтроки = строки.filter((строка) => отмечено.has(строка.id));
  const суммаОтмеченных = отмеченныеСтроки.reduce((итог, строка) => итог + строка.amount, 0);
  const всеОтмечены = строки.length > 0 && отмеченныеСтроки.length === строки.length;

  function переключить(claimId: string, checked: boolean) {
    setОтмечено((прежние) => {
      const новые = new Set(прежние);
      if (checked) {
        новые.add(claimId);
      } else {
        новые.delete(claimId);
      }
      return новые;
    });
  }

  async function принятьОдну(claim: ClaimOut) {
    setЗанята(claim.id);
    try {
      await приёмка.mutateAsync({ claimId: claim.id, decision: 'accept' });
      // Принятая заявка уходит из очереди — снимаем и её отметку, иначе набор копит призраков.
      setОтмечено((прежние) => {
        if (!прежние.has(claim.id)) {
          return прежние;
        }
        const новые = new Set(прежние);
        новые.delete(claim.id);
        return новые;
      });
    } finally {
      setЗанята(null);
    }
  }

  /**
   * Отклонение и возврат на доработку прямо из строки.
   *
   * Тот же путь, что у приёмки: гвард по ключу строки и никакого оптимизма — решение по
   * заявке двигает чужие деньги, и «отклонено» до ответа сервера было бы неправдой.
   *
   * Отказ сервера НЕ гасит раскрытое поле причины: человек только что её напечатал, и
   * стереть её вместе с формой значило бы заставить печатать заново. Сама ошибка приезжает
   * полосой над списком — там же, где ошибка приёмки.
   */
  async function решитьОдну(claim: ClaimOut, вид: РешениеСПричиной, причина: string) {
    setЗанята(claim.id);
    try {
      await приёмка.mutateAsync({ claimId: claim.id, decision: вид, reason: причина });
      setОтмечено((прежние) => {
        if (!прежние.has(claim.id)) {
          return прежние;
        }
        const новые = new Set(прежние);
        новые.delete(claim.id);
        return новые;
      });
    } catch {
      // Сообщение уже разобрано мутацией и стоит над списком конвертом сервера. Второй
      // рассказ о той же ошибке рядом — это два сообщения об одном, между которыми человек
      // ищет разницу.
    } finally {
      setЗанята(null);
    }
  }

  async function принятьПачку() {
    const итог = await пачка.mutateAsync(отмеченныеСтроки.map((строка) => строка.id));
    // Пропущенное проговаривается словами. Молча проглотить пропуск нельзя: человек отметил
    // двадцать, принялось семнадцать, и без объяснения он считает, что три потерялись.
    const хвост =
      итог.skipped > 0
        ? ` Пропущено ${итог.skipped}: ${Object.values(итог.skipped_reasons).join('; ')}.`
        : '';
    const долги = итог.debts_created > 0 ? ` Заведено долгов: ${итог.debts_created}.` : '';
    setИтогПачки(
      `Принято ${итог.accepted} на ${formatKopecks(итог.accepted_amount)}.${долги}${хвост}`,
    );
    setОтмечено(new Set());
  }

  return (
    <ListPane
      // Ноль до приезда данных читается как «заявок нет» — то есть «разбирать нечего».
      caption={данные === undefined ? 'Очередь приёмки' : countLabel(данные.total, CLAIM_FORMS)}
      sliceAt={срез}
      width='имена'
      appliedFilters={[
        безЧека ? 'без чека' : null,
        отСуммы === undefined ? null : 'только крупные',
        круглые ? 'круглые суммы' : null,
      ].filter((имя): имя is string => имя !== null)}
      filters={
        // Переключатель стоит СПРАВА от подписи, как во всех остальных отборах продукта. В
        // соседнем представлении того же раздела он стоял слева, и два представления одного
        // раздела читались как сделанные разными руками.
        <>
          <label className='flex items-center justify-between gap-3 text-paragraph-sm text-text-sub-600'>
            Только без чека
            <Switch.Root
              checked={безЧека}
              onCheckedChange={(checked) => onFilter(FILTER_RECEIPT, checked ? RECEIPT_NONE : null)}
            />
          </label>
          <label className='flex items-center justify-between gap-3 text-paragraph-sm text-text-sub-600'>
            Только крупные, от {formatRubles(КРУПНАЯ)}
            <Switch.Root
              checked={отСуммы !== undefined}
              onCheckedChange={(checked) =>
                onFilter(FILTER_MIN_AMOUNT, checked ? String(КРУПНАЯ) : null)
              }
            />
          </label>
          {/* Тз, раздел 5.6, называет этот отбор СПРАВОЧНЫМ и добавляет «без искусственного
              интеллекта». Он ничего не доказывает: просто чек ровно на 5 000,00 руками пишут
              чаще, чем получают в магазине, и такие заявки смотрят внимательнее. */}
          <label className='flex items-center justify-between gap-3 text-paragraph-sm text-text-sub-600'>
            Только круглые суммы
            <Switch.Root
              checked={круглые}
              onCheckedChange={(checked) => onFilter(FILTER_ROUND, checked ? ROUND_ON : null)}
            />
          </label>
        </>
      }
    >
      {итогПачки !== null && (
        <p className='border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-2 text-paragraph-sm text-text-sub-600'>
          {итогПачки}
        </p>
      )}
      {приёмка.error !== null && (
        <div className='px-4 py-2'>
          <ErrorAlert error={приёмка.error as never} />
        </div>
      )}

      {можноПринимать && строки.length > 0 && (
        <div className='flex items-center justify-between gap-2 border-b border-stroke-soft-200 px-4 py-2'>
          <CheckboxField
            id='очередь-отметить-все'
            label={`Отметить все ${строки.length}`}
            checked={всеОтмечены}
            onCheckedChange={(отмечено) =>
              setОтмечено(отмечено ? new Set(строки.map((строка) => строка.id)) : new Set())
            }
          />
          {отмеченныеСтроки.length > 0 && (
            <Button.Root
              variant='primary'
              mode='filled'
              size='xxsmall'
              onClick={() => void принятьПачку()}
              disabled={пачка.isPending}
            >
              {пачка.isPending
                ? 'Принимаем…'
                : `Принять ${отмеченныеСтроки.length} на ${formatKopecks(суммаОтмеченных)}`}
            </Button.Root>
          )}
        </div>
      )}

      <ScreenState<ListResponse<ClaimOut>>
        data={данные}
        error={запрос.error as never}
        subject='очередь заявок на приёмку'
        isEmpty={(значение) => значение.items.length === 0}
        loading={<ListSkeleton />}
        empty={
          <Пусто
            фильтруем={безЧека || отСуммы !== undefined}
            снятьОтбор={() => {
              onFilter(FILTER_RECEIPT, null);
              onFilter(FILTER_MIN_AMOUNT, null);
              onFilter(FILTER_ROUND, null);
            }}
          />
        }
        onRetry={() => void запрос.refetch()}
      >
        {(значение) => (
          <ul>
            {значение.items.map((заявка) => (
              <li key={заявка.id}>
                <ClaimRow
                  claim={заявка}
                  checked={отмечено.has(заявка.id)}
                  busy={занята === заявка.id}
                  canDecide={можноПринимать}
                  canReject={можноОтклонять}
                  onToggle={(checked) => переключить(заявка.id, checked)}
                  onAccept={() => void принятьОдну(заявка)}
                  onDecide={(вид, причина) => void решитьОдну(заявка, вид, причина)}
                  onOpen={() => onOpenClaim(заявка.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </ScreenState>
    </ListPane>
  );
}

// См. пояснение в AccountableDesk: причина без действия оставляет человека искать глазами,
// какой из двух переключателей виноват.
function Пусто({ фильтруем, снятьОтбор }: { фильтруем: boolean; снятьОтбор: () => void }) {
  return фильтруем ? (
    <EmptyState
      icon={RiFileList3Line}
      title='Под этот отбор заявок нет'
      description='Список пуст из-за отбора, а не потому, что заявок нет вовсе.'
      action={
        <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={снятьОтбор}>
          <Button.Icon as={RiFilterOffLine} />
          Снять отбор
        </Button.Root>
      }
    />
  ) : (
    // Не «все заявки разобраны»: пустая очередь бывает и тогда, когда разбирать было нечего
    // (после очистки данных 05.10.2026 — ровно так), и обещать проделанную работу нельзя.
    <EmptyState
      icon={RiFileList3Line}
      title='Очередь пуста'
      illustration='заявки'
      description='Отчётов на приёмку нет. Отчёт появится здесь, когда человек с деньгами под отчётом отчитается чеком в приложении.'
    />
  );
}
